import { renderRulebookUserText, type RulebookDisplayedSnapshot, useTranslation } from "@next-platform/client-common-core";
import hudTileText from "@next-platform/client-common-core/rulebook/hud/hudTileText";
import resolveHudMeterValue from "@next-platform/client-common-core/rulebook/hud/resolveHudMeterValue";
import resolveHudMood, { COMBINED_MOOD_COLOR } from "@next-platform/client-common-core/rulebook/hud/resolveHudMood";
import type { RulebookPublicView } from "@next-platform/packages-ts-types";
import { useTheme as useNextTheme } from "next-themes";
import React, { useCallback, useMemo } from "react";

import { Theme } from "@/constants/theme";

import { HUD_MUTED } from "./hudClassNames";

type RulebookHudCaptionProps = {
  rulebook: RulebookPublicView;
  snapshot: RulebookDisplayedSnapshot;
  characterName?: string;
  /** Restores the meter panel (clears `hud_hidden`). */
  onShow: () => void;
  "data-testid"?: string;
};

/**
 * The one-line stand-in while the meters are hidden: the caption's mood dot and text (or the first visible
 * meter's `label · level`), clickable to bring the panel back — the menu's "Show meters" is easy to miss.
 */
const RulebookHudCaption = ({ rulebook, snapshot, characterName, onShow, "data-testid": testId = "RulebookHudCaption" }: RulebookHudCaptionProps) => {
  const { t } = useTranslation();
  const theme = useNextTheme().resolvedTheme === Theme.DARK ? "dark" : "light";
  const mood = useMemo(() => resolveHudMood({ rulebook, snapshot, theme }), [rulebook, snapshot, theme]);
  // The public meter view already excludes hidden meters, so the first entry is the fallback candidate.
  const firstMeter = useMemo(() => (rulebook.meters ?? [])[0], [rulebook.meters]);
  const fallbackText = useMemo(() => {
    if (!firstMeter) {
      return "";
    }
    const value = resolveHudMeterValue({ meter: firstMeter, hudMeter: snapshot.meta.meters?.[firstMeter.id] });
    const levelText = (levelNumber: number) => t("chat:rulebook.hud.levelShort", { defaultValue: "Lv {{number}}", number: levelNumber });
    return `${firstMeter.label} · ${hudTileText({ meter: firstMeter, value, clockLabel: snapshot.meta.clock?.label, levelText }).value}`;
  }, [firstMeter, snapshot, t]);
  const caption = mood?.caption ?? fallbackText;
  const renderedCaption = useMemo(() => renderRulebookUserText({ text: caption, characterName }), [caption, characterName]);
  const dotColor = mood?.color ?? COMBINED_MOOD_COLOR;
  const dotStyle = useMemo(() => ({ backgroundColor: dotColor }), [dotColor]);
  const handleShow = useCallback(() => onShow(), [onShow]);
  const showLabel = t("chat:rulebookHud.showHud", { defaultValue: "Show meters" });

  if (!caption) {
    return null;
  }
  return (
    <button
      type="button"
      onClick={handleShow}
      title={showLabel}
      aria-label={showLabel}
      data-testid={`${testId}-Show`}
      className={`flex items-center gap-1.5 border-0 bg-transparent px-4 py-1.5 text-xs ${HUD_MUTED} hover:text-foreground`}
    >
      <span data-testid={`${testId}-MoodDot`} className="h-[7px] w-[7px] flex-none rounded-full" style={dotStyle} />
      <span data-testid={testId} className="min-w-0 truncate">
        {renderedCaption}
      </span>
    </button>
  );
};

export default React.memo(RulebookHudCaption);
